import { useRef, useState } from "react";
import { State } from "redux/store";
import { connect, useDispatch } from "react-redux";
import { QuotaLimitModalData, StepTileProgressStatus } from "types";
import { LimitExceeded } from "extractedBackendTypes";

import StepTile from "../StepTile";
import UploadIcon from "assets/icons/UploadIcon";
import {
  getIsDatasetIndexing,
  getLimitExceededDetails,
  getNewDatasetId,
  getUploadFInished,
  getUploadStatus,
} from "redux/CreateDataset/selectors";
import { renderHeadIcon } from "../helpers";
import { setQuotaLimitModalData } from "redux/Modals/actions";
import { getQuotaLimitModalData } from "redux/Modals/selectors";
import UploadBanner from "./UploadBanner";

import PulsingDropdownIcon from "../PulsingDropdownIcon";
import styles from "./style.module.scss";
import { useFeatureFlagService } from "hooks/useFeatureFlagService";
import { USAGE_REPORT } from "helpers/constants/filters";

const UploadStep = ({
  uploadStatus,
  newDatasetId,
  limitExceededDetails,
  datasetIndexing,
  uploadFinished,
  quotaModalData,
  indexType,
  indexTypeStatus,
}: UploadStepProps) => {
  const usageReportEnabled = useFeatureFlagService(USAGE_REPORT).enabled;
  const [showBanner, setShowBanner] = useState(false);

  // Upload becomes active when index type is complete
  const uploadActive =
    indexTypeStatus === StepTileProgressStatus.COMPLETE &&
    uploadStatus !== StepTileProgressStatus.IN_PROGRESS &&
    uploadStatus !== StepTileProgressStatus.COMPLETE;

  const bannerRef = useRef<HTMLDivElement>(null);
  const actionButtonRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();

  const toggleShowBanner = () => {
    setShowBanner(!showBanner);
  };

  const handlePulsingArrowClick = () => {
    if (
      usageReportEnabled &&
      !!limitExceededDetails &&
      limitExceededDetails.limit_type === "MAX_USER_QUOTA" &&
      !quotaModalData?.allowUpload
    ) {
      dispatch(setQuotaLimitModalData({ ...quotaModalData, show: true }));
    } else {
      setShowBanner(!showBanner);
    }
  };

  const handleOutsideClick = (event: any) => {
    const actionButton = actionButtonRef.current;
    const banner = bannerRef.current;

    const editButtonClicked =
      actionButton && actionButton.contains(event.target);
    const bannerClicked = banner && banner.contains(event.target);

    if ((!actionButton || !editButtonClicked) && !bannerClicked) {
      setShowBanner(false);
    }
  };

  const renderActionIcon = () => {
    if (!showBanner && uploadActive && !datasetIndexing) {
      return (
        <PulsingDropdownIcon
          showPulse={!showBanner}
          isDropdownOpen={showBanner}
          toggleDropdown={handlePulsingArrowClick}
          containerRef={actionButtonRef}
        />
      );
    } else return <></>;
  };

  // Determine the actual upload step status
  const actualUploadStatus =
    uploadStatus === StepTileProgressStatus.IN_PROGRESS ||
    uploadStatus === StepTileProgressStatus.COMPLETE ||
    uploadStatus === StepTileProgressStatus.FAILED
      ? uploadStatus
      : uploadActive
        ? StepTileProgressStatus.ACTIVE
        : StepTileProgressStatus.DISABLED;

  return (
    <div className={styles.uploadContainer}>
      <StepTile
        title="Upload Data"
        status={actualUploadStatus}
        headIcon={renderHeadIcon(<UploadIcon />, actualUploadStatus)}
        actionIcon={renderActionIcon()}
        isContentShown={showBanner}
        handleOutsideClick={handleOutsideClick}
      />

      <UploadBanner
        bannerRef={bannerRef}
        newDatasetId={newDatasetId}
        showBanner={showBanner}
        toggleShowBanner={toggleShowBanner}
        limitExceededDetails={limitExceededDetails}
        uploadFinished={uploadFinished}
        indexType={indexType}
      />
    </div>
  );
};

const mapStateToProps = (state: State) => {
  return {
    uploadStatus: getUploadStatus(state),
    newDatasetId: getNewDatasetId(state),
    limitExceededDetails: getLimitExceededDetails(state),
    datasetIndexing: getIsDatasetIndexing(state),
    uploadFinished: getUploadFInished(state),
    quotaModalData: getQuotaLimitModalData(state),
  };
};

export default connect(mapStateToProps)(UploadStep);

interface UploadStepProps {
  uploadStatus: StepTileProgressStatus;
  limitExceededDetails: LimitExceeded | null;
  newDatasetId: string;
  datasetIndexing: boolean;
  uploadFinished: boolean;
  quotaModalData: QuotaLimitModalData;
  indexType: string;
  indexTypeStatus: StepTileProgressStatus;
}
